<template>
  <OnboardingWrapper>
    <div class="d-flex page-root">
      <navigator class="navigator" :flag="navFlag" />
      
      <div class="addgame-main-container">
        <div class="title-header">
          <h1 class="page-title">Add New Game</h1>
        </div>

        <div class="addgame-viewport">
          <div class="content-wrapper">
            <!-- Loading Overlay -->
            <transition name="fade">
              <Loading v-if="isLoading"></Loading>
            </transition>

            <!-- Image Cropper -->
            <imageCropper
              v-if="cropperOn"
              :img="imgFile.src"
              :imgType="imgFile.type"
              stencilComponent="rectangle-stencil"
              :cropperProps="{aspectRatio: 1/1}"
              @cropConfirmed="handleCropperConfirm"
              @cropCanceled="handleCropperCancel"
            />

            <div class="section-header">Game Information</div>
            <div class="fields-grid">
              <div class="field-card">
                <div class="field-label">Game Name <span style="color: #FC7727">*</span></div>
                <input 
                  v-model="data.gameName" 
                  class="field-input" 
                  placeholder="Enter game name"
                  @input="handleFieldChange" />
              </div>

              <div class="field-card">
                <div class="field-label">Company <span style="color: #FC7727">*</span></div>
                <input 
                  v-model="data.company" 
                  class="field-input" 
                  placeholder="Enter company name"
                  @input="handleFieldChange" />
              </div>

              <div class="field-card">
                <div class="field-label">Resource Link</div>
                <input 
                  v-model="data.resourceLink" 
                  class="field-input" 
                  placeholder="Enter URL to game resources"
                  @input="handleFieldChange" />
              </div>

           

              <div class="field-card full-width">
                <div class="field-label">Description</div>
                <textarea 
                  v-model="data.description" 
                  class="field-input field-textarea" 
                  placeholder="Enter game description"
                  rows="4"
                  @input="handleFieldChange"></textarea>
              </div>

              <div class="field-card full-width image-upload-card">
                <div class="field-label">Game Image</div>
                <div class="image-upload-section">
                  <div class="upload-area" 
                    :class="{ 'drag-active': isDragging }"
                     @click="triggerFileInput" 
                     @dragenter="handleDragEnter"
                     @dragleave="handleDragLeave"
                     @dragover="handleDragOver"
                     @drop="handleDrop">
                    <input 
                      ref="fileInput"
                      type="file" 
                      accept="image/*"
                      @change="uploadImg"
                      style="display: none;" />
                    
                    <div v-if="!data.img || data.img === defaultGameImg" class="upload-placeholder">
                      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                      <p>Click or drag and drop to upload game image</p>
                      <span class="upload-hint">PNG, JPG, GIF up to 10MB</span>
                    </div>
                    
                    <div v-else class="image-preview-container" @mouseenter="shadeOn = true" @mouseleave="shadeOn = false">
                      <button class="remove-img-btn-small" @click.stop="removeImage" @mouseenter="shadeOn = false" title="Remove image">×</button>
                      <img :src="data.img" alt="Game preview" class="preview-image" @mouseenter="shadeOn = true" />
                      <div v-if="shadeOn" class="image-overlay">
                        <p>Click to change image</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Game Tags Section -->
            <div class="section-header">Game Tags</div>
            <div class="section-subtext">What kind of player is this game for? Your tags help us find testers who match.</div>
            <div class="tags-section">
              <!-- Platform Tags -->
              <div class="tag-category">
                <div class="tag-category-header">Platform</div>
                <div class="tag-options">
                  <button
                    v-for="platform in platformOptions"
                    :key="platform"
                    :class="['tag-btn', { 'selected': platformSelected.includes(platform) }]"
                    @click="toggleTag('platform', platform)">
                    {{ platform }}
                  </button>
                  <div v-for="(custom, index) in customPlatformTags" :key="'custom-' + index" class="custom-tag-wrapper">
                    <button
                      :class="['tag-btn', { 'selected': platformSelected.includes(custom) }]"
                      @click="toggleTag('platform', custom)">
                      {{ custom }}
                    </button>
                    <button class="custom-tag-remove" @click="removeCustomTag('platform', custom)" title="Remove tag">×</button>
                  </div>
                  <div v-if="showCustomPlatform" class="custom-tag-input">
                    <input 
                      ref="customPlatformInput"
                      v-model="customPlatformInput"
                      class="custom-tag-field"
                      placeholder="Enter custom platform"
                      @keyup.enter="addCustomTag('platform')"
                      @blur="handleCustomTagInputBlur('platform')" />
                    <button 
                      v-if="customPlatformInput.trim()" 
                      class="custom-tag-confirm" 
                      :class="{ 'is-disabled': !customPlatformInput.trim() }"
                      :disabled="!customPlatformInput.trim()"
                      @mousedown.prevent 
                      @click="addCustomTag('platform')" 
                      title="Save tag">✓</button>
                    <button class="custom-tag-close" @mousedown.prevent @click="handleCustomTagClose('platform')">×</button>
                  </div>
                  <button v-else class="tag-btn other-btn" @click="openCustomTagInput('platform')">Other</button>
                </div>
              </div>

              <!-- Behavior Tags -->
              <div class="tag-category">
                <div class="tag-category-header">Behavior</div>
                <div class="tag-options">
                  <button
                    v-for="behavior in behaviorOptions"
                    :key="behavior"
                    :class="['tag-btn', { 'selected': behaviorSelected.includes(behavior) }]"
                    @click="toggleTag('behavior', behavior)">
                    {{ behavior }}
                  </button>
                  <div v-for="(custom, index) in customBehaviorTags" :key="'custom-' + index" class="custom-tag-wrapper">
                    <button
                      :class="['tag-btn', { 'selected': behaviorSelected.includes(custom) }]"
                      @click="toggleTag('behavior', custom)">
                      {{ custom }}
                    </button>
                    <button class="custom-tag-remove" @click="removeCustomTag('behavior', custom)" title="Remove tag">×</button>
                  </div>
                  <div v-if="showCustomBehavior" class="custom-tag-input">
                    <input 
                      ref="customBehaviorInput"
                      v-model="customBehaviorInput"
                      class="custom-tag-field"
                      placeholder="Enter custom behavior"
                      @keyup.enter="addCustomTag('behavior')"
                      @blur="handleCustomTagInputBlur('behavior')" />
                    <button 
                    class="custom-tag-confirm" 
                    :class="{ 'is-disabled': !customBehaviorInput.trim() }"
                    :disabled="!customBehaviorInput.trim()"
                    @mousedown.prevent 
                    @click="addCustomTag('behavior')" 
                    title="Save tag">✓</button>
                    <button class="custom-tag-close" @mousedown.prevent @click="handleCustomTagClose('behavior')">×</button>
                  </div>
                  <button v-else class="tag-btn other-btn" @click="openCustomTagInput('behavior')">Other</button>
                </div>
              </div>

              <!-- Genre Tags -->
              <div class="tag-category">
                <div class="tag-category-header">Genre</div>
                <div class="tag-options">
                  <button
                    v-for="genre in genreOptions"
                    :key="genre"
                    :class="['tag-btn', { 'selected': genreSelected.includes(genre) }]"
                    @click="toggleTag('genre', genre)">
                    {{ genre }}
                  </button>
                  <div v-for="(custom, index) in customGenreTags" :key="'custom-' + index" class="custom-tag-wrapper">
                    <button
                      :class="['tag-btn', { 'selected': genreSelected.includes(custom) }]"
                      @click="toggleTag('genre', custom)">
                      {{ custom }}
                    </button>
                    <button class="custom-tag-remove" @click="removeCustomTag('genre', custom)" title="Remove tag">×</button>
                  </div>
                  <div v-if="showCustomGenre" class="custom-tag-input">
                    <input 
                      ref="customGenreInput"
                      v-model="customGenreInput"
                      class="custom-tag-field"
                      placeholder="Enter custom genre"
                      @keyup.enter="addCustomTag('genre')"
                      @blur="handleCustomTagInputBlur('genre')" />
                    <button 
                    class="custom-tag-confirm" 
                    :class="{ 'is-disabled': !customGenreInput.trim() }"
                    :disabled="!customGenreInput.trim()"
                    @mousedown.prevent 
                    @click="addCustomTag('genre')"  
                    title="Save tag">✓</button>
                    <button class="custom-tag-close" @mousedown.prevent @click="handleCustomTagClose('genre')">×</button>
                  </div>
                  <button v-else class="tag-btn other-btn" @click="openCustomTagInput('genre')">Other</button>
                </div>
              </div>
            </div>

            <div class="section-header">QA Lead Information</div>
            <div class="fields-grid">
              <div class="field-card">
                <div class="field-label">QA Lead Name</div>
                <input 
                  v-model="data.qaLead.name" 
                  class="field-input" 
                  placeholder="Enter QA lead name"
                  @input="handleFieldChange" />
              </div>

              <div class="field-card">
                <div class="field-label">Email</div>
                <input 
                  v-model="data.qaLead.email" 
                  class="field-input" 
                  type="email"
                  placeholder="Enter email address"
                  @input="handleFieldChange" />
              </div>

              <div class="field-card">
                <div class="field-label">Work Phone</div>
                <input 
                  v-model="data.qaLead.workPhone" 
                  class="field-input" 
                  type="tel"
                  placeholder="Enter work phone"
                  @input="handleFieldChange" />
              </div>

              <div class="field-card">
                <div class="field-label">Mobile</div>
                <input 
                  v-model="data.qaLead.mobile" 
                  class="field-input" 
                  type="tel"
                  placeholder="Enter mobile number"
                  @input="handleFieldChange" />
              </div>
            </div>
            <div class="action-buttons">
              <button 
                class="action-btn cancel-btn" 
                type="button"
                @click="handleCancel">
                CANCEL
              </button>
              <button 
                class="action-btn confirm-btn"
                type="button" 
                @click="handleConfirm">
                ADD GAME
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </OnboardingWrapper>
</template>


<script>
import navigator from '../layout/NavBarNew.vue'
import imageCropper from '../layout/ImageCropper.vue'
import Loading from '../loading/index.vue'
import OnboardingWrapper from '../onboarding/OnboardingWrapper.vue';
import { onboardingService } from '@/services/OnboardingService';
import { initializePageOnboarding } from '@/services/onboardingConfigs';
import GamesObject from "../../Firestore/GamesObject";


const today = new Date();

function getMimeType(file, fallback = null) {
    const byteArray = (new Uint8Array(file)).subarray(0, 4);
    let header = '';
    for (let i = 0; i < byteArray.length; i++) {
        header += byteArray[i].toString(16);
    }
    switch (header) {
        case "89504e47":
            return "image/png";
        case "47494638":
            return "image/gif";
        case "ffd8ffe0":
        case "ffd8ffe1":
        case "ffd8ffe2":
        case "ffd8ffe3":
        case "ffd8ffe8":
            return "image/jpeg";
        default:
            return fallback;
    }
}

export default {
    name: "addgame",
    components: {
        navigator,
        imageCropper,
        Loading,
        OnboardingWrapper,
    },
    data() {
        return {
            msg: "addgame",
            navFlag: -1,
            userType: window.localStorage.getItem("userType") ? parseInt(window.localStorage.getItem("userType")) : -1,
            modify: true,
            shadeOn: false,
            cropperOn: false,
            isLoading: false,
            isDragging: false,
            dragCounter: 0,
            defaultGameImg: require("../../assets/game_img_default.png"),
            imgFile: {
                src: null,
                type: null
            },
            data: {
                gameId: "-1",
                developerId: window.localStorage.getItem("userId"),
                gameName: "",
                company: "",
                resourceLink: "",
                img: "",
                endTime: "",
                description: "",
                platform: [],
                behavior: [],
                genre: [],
                qaLead: {
                    name: "",
                    workPhone: "",
                    mobile: "",
                    email: "",
                }
            },
            // Tag selections
            platformSelected: [],
            behaviorSelected: [],
            genreSelected: [],
            // Custom tag inputs
            showCustomBehavior: false,
            showCustomGenre: false,
            showCustomPlatform: false,
            customBehaviorInput: "",
            customGenreInput: "",
            customPlatformInput: "",
            // Tag options (from AttributeEdit)
            platformOptions: ["Mobile", "Game Console", "PC/Laptop", "Handheld", "VR", "Web"],
            behaviorOptions: ["Competition", "Completion", "Community/Social", "Design", "Destruction", "Fantasy", "Story"],
            genreOptions: ["Action", "Adventure", "Casual/Puzzle", "Multiplayer Online", "Role Playing Game", "Shooter", "Simulation", "Sports", "Strategy"],
            
            assignYearSelected: "",
            assignMonthSelected: "",
            assignDaySelected: "",
            assignHourSelected: 0,
            assignYearSelection: [],
            assignDaySelection: [],
            assignHourSelection: [
                { timeText: "0:00 AM", timeString: "00:00:00" },
                { timeText: "1:00 AM", timeString: "01:00:00" },
                { timeText: "2:00 AM", timeString: "02:00:00" },
                { timeText: "3:00 AM", timeString: "03:00:00" },
                { timeText: "4:00 AM", timeString: "04:00:00" },
                { timeText: "5:00 AM", timeString: "05:00:00" },
                { timeText: "6:00 AM", timeString: "06:00:00" },
                { timeText: "7:00 AM", timeString: "07:00:00" },
                { timeText: "8:00 AM", timeString: "08:00:00" },
                { timeText: "9:00 AM", timeString: "09:00:00" },
                { timeText: "10:00 AM", timeString: "10:00:00" },
                { timeText: "11:00 AM", timeString: "11:00:00" },
                { timeText: "12:00 PM", timeString: "12:00:00" },
                { timeText: "1:00 PM", timeString: "13:00:00" },
                { timeText: "2:00 PM", timeString: "14:00:00" },
                { timeText: "3:00 PM", timeString: "15:00:00" },
                { timeText: "4:00 PM", timeString: "16:00:00" },
                { timeText: "5:00 PM", timeString: "17:00:00" },
                { timeText: "6:00 PM", timeString: "18:00:00" },
                { timeText: "7:00 PM", timeString: "19:00:00" },
                { timeText: "8:00 PM", timeString: "20:00:00" },
                { timeText: "9:00 PM", timeString: "21:00:00" },
                { timeText: "10:00 PM", timeString: "22:00:00" },
                { timeText: "11:00 PM", timeString: "23:00:00" }
            ],
            assignMonthSelection: [
                { monthText: "January", monthString: "01" },
                { monthText: "February", monthString: "02" },
                { monthText: "March", monthString: "03" },
                { monthText: "April", monthString: "04" },
                { monthText: "May", monthString: "05" },
                { monthText: "June", monthString: "06" },
                { monthText: "July", monthString: "07" },
                { monthText: "August", monthString: "08" },
                { monthText: "September", monthString: "09" },
                { monthText: "October", monthString: "10" },
                { monthText: "November", monthString: "11" },
                { monthText: "December", monthString: "12" }
            ],
        }
    },
    computed: {
        themeColor() {
            return '#FC7727';
        },
        themeColorLight() {
            return 'rgba(252, 119, 39, 0.3)';
        },
        customBehaviorTags() {
            return this.behaviorSelected.filter(tag => !this.behaviorOptions.includes(tag));
        },
        customGenreTags() {
            return this.genreSelected.filter(tag => !this.genreOptions.includes(tag));
        },
        customPlatformTags() {
            return this.platformSelected.filter(tag => !this.platformOptions.includes(tag));
        }
    },
    mounted() {
        if (window.localStorage.getItem("userType")) {
            this.userType = parseInt(window.localStorage.getItem("userType"));
            if (this.userType === 1) {
                this.$nextTick(() => {
                    initializePageOnboarding('addGame', this.userType, onboardingService, this.$store);
                });
            }
            this.updateColor();
        } else {
            this.$router.push({ name: 'login' })
        }

        // Initialize date selections
        let curYear = new Date().getFullYear();
        for (let i = 0; i < 10; i++) {
            this.assignYearSelection.push({yearText: (curYear + i).toString()})
        }
        this.assignYearSelected = today.getFullYear();
        this.assignMonthSelected = "01";

        let totalDays = new Date(parseInt(this.assignYearSelected), parseInt(this.assignMonthSelected), 0).getDate();
        this.assignDaySelection = [];
        for (let i = 1; i <= totalDays; i++) {
            let date = i.toString();
            if (i < 10) {
                date = "0" + date;
            }
            this.assignDaySelection.push({dayText: date});
        }
        this.assignDaySelected = "01";
    },
    methods: {
        handleFieldChange() {
            this.modify = true;
        },

        toggleTag(category, tag) {
            if (category === 'platform') {
                const index = this.platformSelected.indexOf(tag);
                if (index > -1) {
                    this.platformSelected.splice(index, 1);
                } else {
                    this.platformSelected.push(tag);
                }
            } else if (category === 'behavior') {
                const index = this.behaviorSelected.indexOf(tag);
                if (index > -1) {
                    this.behaviorSelected.splice(index, 1);
                } else {
                    this.behaviorSelected.push(tag);
                }
            } else if (category === 'genre') {
                const index = this.genreSelected.indexOf(tag);
                if (index > -1) {
                    this.genreSelected.splice(index, 1);
                } else {
                    this.genreSelected.push(tag);
                }
            }
            this.handleFieldChange();
        },

        addCustomTag(category) {
            let input = "";
            let targetArray = null;
            
            if (category === 'behavior') {
                input = this.customBehaviorInput.trim();
                targetArray = this.behaviorSelected;
                if (input && !targetArray.includes(input)) {
                    targetArray.push(input);
                    this.customBehaviorInput = "";
                    this.showCustomBehavior = false;
                    this.handleFieldChange();
                }
            } else if (category === 'genre') {
                input = this.customGenreInput.trim();
                targetArray = this.genreSelected;
                if (input && !targetArray.includes(input)) {
                    targetArray.push(input);
                    this.customGenreInput = "";
                    this.showCustomGenre = false;
                    this.handleFieldChange();
                }
            }
            
            else if (category === 'platform') {
                input = this.customPlatformInput.trim();
                targetArray = this.platformSelected;
                if (input && !targetArray.includes(input)) {
                    targetArray.push(input);
                    this.customPlatformInput = "";
                    this.showCustomPlatform = false;
                    this.handleFieldChange();
                }
            }
            
        },

        handleCustomTagInputBlur(category) {
            // Clear the input and close when clicking off
            if (category === 'behavior') {
                this.customBehaviorInput = "";
                this.showCustomBehavior = false;
            } else if (category === 'genre') {
                this.customGenreInput = "";
                this.showCustomGenre = false;
            } else if (category === 'platform') {
                this.customPlatformInput = "";
                this.showCustomPlatform = false;
            }
        },

        handleCustomTagClose(category) {
            if (category === 'behavior') {
                this.customBehaviorInput = "";
            } else if (category === 'genre') {
                this.customGenreInput = "";
            }
            else if (category === 'platform') {
                this.customPlatformInput = "";
            }
        },

        removeCustomTag(category, tag) {
            if (category === 'behavior') {
                const index = this.behaviorSelected.indexOf(tag);
                if (index > -1) {
                    this.behaviorSelected.splice(index, 1);
                    this.handleFieldChange();
                }
            } else if (category === 'genre') {
                const index = this.genreSelected.indexOf(tag);
                if (index > -1) {
                    this.genreSelected.splice(index, 1);
                    this.handleFieldChange();
                }
            }
            else if (category === 'platform') {
                const index = this.platformSelected.indexOf(tag);
                if (index > -1) {
                    this.platformSelected.splice(index, 1);
                    this.handleFieldChange();
                }
            }
        },

        cancelCustomTag(category) {
            if (category === 'behavior') {
                this.customBehaviorInput = "";
                this.showCustomBehavior = false;
            } else if (category === 'genre') {
                this.customGenreInput = "";
                this.showCustomGenre = false;
            }
            else if (category === 'platform') {
                this.customPlatformInput = "";
                this.showCustomPlatform = false;
            }
        },

        openCustomTagInput(category) {
            if (category === 'behavior') {
                this.showCustomBehavior = true;
                this.$nextTick(() => {
                    if (this.$refs.customBehaviorInput) {
                        this.$refs.customBehaviorInput.focus();
                    }
                });
            } else if (category === 'genre') {
                this.showCustomGenre = true;
                this.$nextTick(() => {
                    if (this.$refs.customGenreInput) {
                        this.$refs.customGenreInput.focus();
                    }
                });
            } else if (category === 'platform') {
                this.showCustomPlatform = true;
                this.$nextTick(() => {
                    if (this.$refs.customPlatformInput) {
                        this.$refs.customPlatformInput.focus();
                    }
                });
            }
        },

        triggerFileInput() {
            this.$refs.fileInput.click();
        },

        uploadImg(event) {
            const { files } = event.target;
            if (files && files[0]) {
                this.processFile(files[0]);
            }
        },

        processFile(file) {
            if (!file.type.startsWith('image/')) {
                alert('Please upload an image file (PNG, JPG, GIF).');
                return;
            }
            if (this.imgFile.src) {
                URL.revokeObjectURL(this.imgFile.src)
            }
            const blob = URL.createObjectURL(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                this.imgFile = {
                    src: blob,
                    type: getMimeType(e.target.result, file.type),
                };
            };
            reader.readAsArrayBuffer(file);
            this.cropperOn = true;
            this.handleFieldChange();
        },

        handleDragEnter(event) {
            event.preventDefault();
            this.dragCounter++;
            this.isDragging = true;
        },

        handleDragLeave(event) {
            event.preventDefault();
            this.dragCounter--;
            if (this.dragCounter === 0) {
                this.isDragging = false;
            }
        },

        handleDragOver(event) {
            event.preventDefault();
        },

        handleDrop(event) {
            event.preventDefault();
            this.dragCounter = 0;
            this.isDragging = false;
            const files = event.dataTransfer.files;
            if (files && files[0]) {
                this.processFile(files[0]);
            }
        },

        handleCropperConfirm(imgBlob) {
            this.cropperOn = false;
            const reader = new FileReader();
            reader.readAsDataURL(imgBlob);
            reader.onload = () => {
                this.data.img = reader.result;
            };
        },

        handleCropperCancel() {
            this.cropperOn = false;
        },

        removeImage() {
            const confirmed = window.confirm('Are you sure you want to remove this image?');
            if (confirmed) {
                this.data.img = "";
                this.handleFieldChange();
            }
        },

        handleSelect(box, event) {
            const idx = event.target.selectedIndex;
            if (box == "year") {
                this.assignYearSelected = this.assignYearSelection[idx].yearText;
                if (this.assignMonthSelected != "") {
                    let totalDays = new Date(parseInt(this.assignYearSelected), parseInt(this.assignMonthSelected), 0).getDate();
                    this.assignDaySelection = [];
                    for (let i = 1; i <= totalDays; i++) {
                        let date = i.toString();
                        if (i < 10) {
                            date = "0" + date;
                        }
                        this.assignDaySelection.push({dayText: date});
                    }
                }
            } else if (box == "month") {
                this.assignMonthSelected = this.assignMonthSelection[idx].monthString;
                if (this.assignYearSelected != "") {
                    let totalDays = new Date(parseInt(this.assignYearSelected), parseInt(this.assignMonthSelected), 0).getDate();
                    this.assignDaySelection = [];
                    for (let i = 1; i <= totalDays; i++) {
                        let date = i.toString();
                        if (i < 10) {
                            date = "0" + date;
                        }
                        this.assignDaySelection.push({dayText: date});
                    }
                }
            } else if (box == "day") {
                this.assignDaySelected = this.assignDaySelection[idx].dayText;
            } else {
                this.assignHourSelected = idx;
            }
            this.handleFieldChange();
        },

        handleCancel() {
            if (this.modify) {
                const answer = window.confirm('Do you really want to cancel? All unsaved changes will be lost!');
                if (answer) this.$router.go(-1);
            } else {
                this.$router.go(-1);
            }
        },

        handleConfirm() {
            // Validation
            if (!this.data.gameName || this.data.gameName.trim() === "") {
                alert('Please fill in required field: Game Name');
                return;
            }
            
            if (!this.data.company || this.data.company.trim() === "") {
                alert('Please fill in required field: Company');
                return;
            }

            // Build endTime string
            this.data.endTime = this.assignYearSelected + "-" + this.assignMonthSelected + "-" + this.assignDaySelected + "T" + this.assignHourSelection[this.assignHourSelected].timeString;
            
            // Set default image if none uploaded
            if (this.data.img == "") {
                this.data.img = this.defaultGameImg;
            }

            // Add tags to data object
            this.data.platform = [...this.platformSelected];
            this.data.behavior = [...this.behaviorSelected];
            this.data.genre = [...this.genreSelected];

            this.isLoading = true;
            
            // Call the API
            GamesObject.createProject(
                this.data.gameId,
                window.localStorage.getItem("userId"),
                this.data.gameName,
                this.data.company,
                this.data.img,
                this.data.endTime,
                this.data.description,
                this.data.qaLead,
                this.data.platform,
                this.data.behavior,
                this.data.genre
            )
            .then(res => {
                console.log('Game created successfully:', res);
                this.isLoading = false;
                this.modify = false;
                
                // Redirect to dashboard
                this.$router.push({
                    name: 'dashboard',
                });
            })
            .catch(error => {
                console.log('Error creating game:', error);
                this.isLoading = false;
                alert('Error creating game. Please try again.');
            });
        },

        updateColor() {
            var r = document.querySelector(':root');
            r.style.setProperty('--THEME-COLOR', this.themeColor);
            r.style.setProperty('--THEME-COLOR-LIGHT', this.themeColorLight);
        }
    },
    beforeRouteLeave(to, from, next) {
        if (this.modify) {
            const answer = window.confirm('Do you really want to leave? All unsaved changes will be lost!')
            if (answer) next(); else next(false);
        } else {
            next();
        }
    },
    destroyed() {
        if (this.imgFile.src) {
            URL.revokeObjectURL(this.imgFile.src)
        }
    }
}
</script>

<style scoped>
.page-root {
    background-color: #EEEEEE;
    height: 100vh;
    width: 100%;
}

.addgame-main-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100vh;
    overflow: hidden;
}

.title-header {
    background-color: white;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 80px;
    width: 100%;
    box-shadow: 0 4px 10px -6px grey;
    z-index: 5;
}

.page-title {
    color: #FF7222;
    font-weight: bold;
    font-size: 1.8rem;
    margin: 0;
}

.addgame-viewport {
    flex: 1;
    overflow-y: auto;
    padding: 40px 5%;
}

.content-wrapper {
    padding: 30px;
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    position: relative;
}

.section-header {
    font-size: 24px;
    font-weight: 600;
    margin: 30px 0 20px 0;
    padding-bottom: 10px;
    border-bottom: 2px solid var(--THEME-COLOR, #FC7727);
    color: var(--THEME-COLOR, #FC7727);
}

.section-subtext {
    font-size: 14px;
    color: #999;
    margin: -12px 0 20px 0;
}

.section-header:first-of-type {
    margin-top: 0;
}

.fields-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
}

.field-card {
    background: rgba(255, 255, 255, 0.9);
    border: 2px solid transparent;
    border-radius: 12px;
    padding: 20px;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    min-height: 80px;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.field-card.full-width {
    grid-column: 1 / -1;
}

.field-card:hover {
    border-color: var(--THEME-COLOR, #FC7727);
    box-shadow: 0 4px 16px var(--THEME-COLOR-LIGHT, rgba(252, 119, 39, 0.3));
    transform: translateY(-2px);
}

.field-label {
    font-size: 12px;
    color: #666;
    text-transform: uppercase;
    font-weight: 600;
    margin-bottom: 8px;
    letter-spacing: 0.5px;
    display: flex;
}
.field-label > div {
    margin-left: auto; 
}
.field-label .action-btn{
    padding:6px 12px;
    min-width: 10px;
}
.field-input {
    border: 1px solid #ddd;
    outline: none;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 16px;
    color: #333;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
    width: 100%;
}

.field-input:focus {
    border-color: var(--THEME-COLOR, #FC7727);
    box-shadow: 0 0 0 3px var(--THEME-COLOR-LIGHT, rgba(252, 119, 39, 0.1));
}

.field-input::placeholder {
    color: #999;
}

.field-textarea {
    resize: vertical;
    min-height: 100px;
    font-family: inherit;
}

.image-upload-card {
    min-height: auto;
}

.image-upload-section {
    width: 100%;
}

.upload-area {
    border: 2px dashed #ddd;
    border-radius: 8px;
    padding: 40px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background: rgba(249, 249, 249, 0.5);
    position: relative;
}

.upload-area.drag-active {
    border-color: var(--THEME-COLOR, #FC7727);
    background: rgba(252, 119, 39, 0.1);
}

.upload-area:hover {
    border-color: var(--THEME-COLOR, #FC7727);
    background: rgba(252, 119, 39, 0.05);
}

.upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    color: #666;
}

.upload-placeholder svg {
    color: var(--THEME-COLOR, #FC7727);
    opacity: 0.6;
}

.upload-placeholder p {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: #333;
}

.upload-hint {
    font-size: 13px;
    color: #999;
}

.image-preview-container {
    position: relative;
    display: inline-block;
    max-width: 100%;
}

.remove-img-btn-small {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 32px;
    height: 32px;
    background-color: #ff4444;
    color: white;
    border: none;
    border-radius: 50%;
    font-size: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    padding: 0;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    z-index: 10;
}

.remove-img-btn-small:hover {
    background-color: #cc0000;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.preview-image {
    max-width: 300px;
    max-height: 300px;
    border-radius: 8px;
    display: block;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 500;
}



/* Date Selection */
.date-selection-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 15px;
}

.date-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.date-item label {
    font-size: 12px;
    color: #666;
    font-weight: 600;
}

.date-select {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    background: white;
    font-size: 14px;
    color: #333;
    cursor: pointer;
    transition: border-color 0.3s ease;
}

.date-select:focus {
    outline: none;
    border-color: var(--THEME-COLOR, #FC7727);
}

/* Tags Section */
.tags-section {
    margin-bottom: 30px;
}

.tag-category {
    margin-bottom: 25px;
}

.tag-category-header {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e0e0e0;
}

.tag-options {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.tag-btn {
    padding: 8px 16px;
    border: 2px solid #e0e0e0;
    background: white;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 14px;
    color: #4b4b4d;
    font-weight: 500;
}

.tag-btn:hover {
    border-color: var(--THEME-COLOR, #FC7727);
    background: rgba(252, 119, 39, 0.05);
}

.tag-btn.selected {
    background: var(--THEME-COLOR, #FC7727);
    color: white;
    border-color: var(--THEME-COLOR, #FC7727);
}

.other-btn {
    background: rgba(252, 119, 39, 0.1);
    border-color: var(--THEME-COLOR, #FC7727);
    color: var(--THEME-COLOR, #FC7727);
    font-weight: 600;
}

.other-btn:hover {
    background: rgba(252, 119, 39, 0.2);
}

.custom-tag-wrapper {
    position: relative;
    display: inline-block;
}

.custom-tag-remove {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 20px;
    height: 20px;
    background-color: #ff4444;
    color: white;
    border: none;
    border-radius: 50%;
    font-size: 14px;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    z-index: 2;
    transition: all 0.2s ease;
}

.custom-tag-remove:hover {
    background-color: #cc0000;
    transform: scale(1.1);
}

.custom-tag-input {
    display: flex;
    align-items: center;
    position: relative;
}

.custom-tag-field {
    border: 2px solid var(--THEME-COLOR, #FC7727);
    outline: none;
    background: white;
    border-radius: 20px;
    padding: 6px 60px 6px 14px;
    font-size: 14px;
    color: #333;
    transition: all 0.2s ease;
    min-width: 150px;
    flex: 1;
}

.custom-tag-field:focus {
    box-shadow: 0 0 0 3px rgba(252, 119, 39, 0.2);
    border-color: #FF8C00;
}

.custom-tag-close {
    position: absolute;
    right: 28px;
    background: none;
    border: none;
    color: #999;
    font-size: 20px;
    cursor: pointer;
    padding: 4px 4px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    z-index: 1;
}

.custom-tag-close:hover {
    color: #ff4444;
    transform: scale(1.2);
}

.custom-tag-confirm {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    color: #4caf50;
    font-size: 18px;
    cursor: pointer;
    padding: 4px 4px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    z-index: 1;
}

.custom-tag-confirm:hover {
    color: #45a049;
    transform: scale(1.2);
}

.action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 15px;
    padding-top: 30px;
    border-top: 1px solid #eee;
    margin-top: auto;
}

.action-btn {
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: white;
    min-width: 120px;
}

.confirm-btn {
    background-color: var(--THEME-COLOR, #FC7727);
}

.cancel-btn {
    background-color: #999;
}

.confirm-btn:hover {
    background: #FF8C00;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px var(--THEME-COLOR-LIGHT, rgba(252, 119, 39, 0.3));
}

.cancel-btn:hover {
    background: #777;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Loading overlay */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}

.addgame-viewport::-webkit-scrollbar {
    display: none;
}

@media (max-width: 768px) {
    .addgame-viewport {
        padding: 20px 2%;
    }
    .content-wrapper {
        padding: 20px;
    }
    .fields-grid {
        grid-template-columns: 1fr;
    }
    .action-buttons {
        flex-direction: column;
    }
    .action-btn {
        width: 100%;
    }
    .section-header {
        font-size: 20px;
    }
    .upload-area {
        padding: 30px 20px;
    }
    .date-selection-grid {
        grid-template-columns: 1fr 1fr;
    }
}

@media (max-width: 1024px) {
    .fields-grid {
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    }
}
</style>


// WEBPACK FOOTER //
// src/components/game-dashboard/AddGame.vue